/**
 * IntakeInputs — shared, reusable form primitives for the intake redesign.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY (L1A intake redesign, 2026-06-21 — build spec §6 "Cross-cutting"):
 *    • Combobox  — a CUSTOM type-to-pick + "Add new" dropdown. The native
 *      `<datalist>` renders white-on-white text on some platforms (the owner's
 *      readability bug). This is dark-ink-on-white, large hit areas, mouse +
 *      keyboard, and an optional "Add «typed»" affordance. Used for Country,
 *      City, Floor, Place/room, Relation/Role.
 *    • DateMask  — one box, auto-slashing `DD / MM / YYYY` as digits arrive.
 *    • TimeMask  — one box, `HH : MM` 24-hr to the exact minute (00–59).
 *
 *  All three are CONTROLLED + presentational. They are built once here so every
 *  intake tier (L1A now; L1B/L2x/L3x later) reuses them with no rebuild.
 *
 *  The mask LOGIC is pulled out as PURE exported helpers (`maskDate`,`maskTime`)
 *  so the Node `.cjs` test runner can pin them without a DOM — the React wrappers
 *  are thin.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useId, useRef, useState } from 'react';
import { maskDate, maskTime } from '../lib/intakeMasks';

export { maskDate, maskTime } from '../lib/intakeMasks';

/* ───────────────────────────── shared styles ───────────────────────────── */

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

const ddStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 2px)',
  left: 0,
  right: 0,
  background: 'var(--surface)',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  boxShadow: '0 8px 22px rgba(0,0,0,.12)',
  zIndex: 40,
  maxHeight: 220,
  overflow: 'auto',
};

/* ──────────────────────────────── Combobox ─────────────────────────────── */

export interface ComboboxProps {
  value: string;
  onChange: (v: string) => void;
  /** The pickable options (already resolved by the caller — labels === values). */
  options: string[];
  placeholder?: string;
  /** Allow adding the typed value as a new option ("+ Add «typed»"). */
  allowAdd?: boolean;
  /** Max options to render in the dropdown (default 12). */
  limit?: number;
  ariaLabel?: string;
  disabled?: boolean;
}

/**
 * A custom type-to-pick combobox. Dark ink on white (readable), mouse + keyboard,
 * optional "Add «typed»". The input value IS the field value (free text is allowed
 * so a typed-but-unpicked value is never lost) — picking an option just sets it.
 */
export function Combobox({
  value,
  onChange,
  options,
  placeholder,
  allowAdd = false,
  limit = 12,
  ariaLabel,
  disabled,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listId = useId();

  const q = (value || '').trim().toLowerCase();
  const filtered = options
    .filter((o) => !q || o.toLowerCase().includes(q))
    .slice(0, limit);
  const exact = options.some((o) => o.toLowerCase() === q);
  const showAdd = allowAdd && !!q && !exact;
  const rows: { label: string; isAdd: boolean }[] = [
    ...filtered.map((o) => ({ label: o, isAdd: false })),
    ...(showAdd ? [{ label: value, isAdd: true }] : []),
  ];

  function pick(label: string) {
    onChange(label);
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && active >= 0 && rows[active]) {
        e.preventDefault();
        pick(rows[active].label);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        role="combobox"
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 160);
        }}
      />
      {open && rows.length > 0 && (
        <div id={listId} role="listbox" style={ddStyle}>
          {rows.map((r, i) => (
            <div
              key={(r.isAdd ? 'add:' : 'opt:') + r.label}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                // mousedown (not click) so it fires before the input blur closes the list
                e.preventDefault();
                if (blurTimer.current) clearTimeout(blurTimer.current);
                pick(r.label);
              }}
              onMouseEnter={() => setActive(i)}
              style={{
                padding: '8px 11px',
                fontSize: 13,
                color: r.isAdd ? 'var(--accent)' : 'var(--ink)',
                fontWeight: r.isAdd ? 700 : 400,
                cursor: 'pointer',
                borderBottom: '1px solid var(--rule)',
                background: i === active ? 'var(--accent-wash)' : 'transparent',
              }}
            >
              {r.isAdd ? (
                <>
                  + Add “{r.label}” <span style={{ color: 'var(--ink-mute)' }}>(new)</span>
                </>
              ) : (
                r.label
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────── DateMask ─────────────────────────────── */

export interface MaskInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

/** One-box date input, auto-formatting `DD / MM / YYYY` as digits are entered. */
export function DateMask({ value, onChange, placeholder = 'DD / MM / YYYY', ariaLabel, disabled }: MaskInputProps) {
  return (
    <input
      style={inputStyle}
      value={value}
      inputMode="numeric"
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      onChange={(e) => onChange(maskDate(e.target.value))}
    />
  );
}

/** One-box time input, auto-formatting `HH : MM` (24-hr, exact minute). */
export function TimeMask({ value, onChange, placeholder = 'HH : MM', ariaLabel, disabled }: MaskInputProps) {
  return (
    <input
      style={inputStyle}
      value={value}
      inputMode="numeric"
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      onChange={(e) => onChange(maskTime(e.target.value))}
    />
  );
}
